/**
 * Responsive Layout Analyzer
 * Analyzes absolute positioned components and generates responsive FlexContainer layouts
 */

import { Component, ComponentType, ResponsivePosition, Viewport, PositioningStrategy } from '@/types';
import { v4 as uuidv4 } from 'uuid';

export interface LayoutPattern {
  type: 'row' | 'column' | 'grid' | 'stack';
  components: string[]; // Component IDs
  bounds: {
    x: number;
    y: number;
    width: number;
    height: number;
  };
  spacing: {
    horizontal: number;
    vertical: number;
  };
  alignment: {
    horizontal: 'left' | 'center' | 'right' | 'space-between' | 'space-around';
    vertical: 'top' | 'center' | 'bottom' | 'stretch';
  };
}

export interface FlexContainerConfig {
  id: string;
  type: 'flex-container';
  name: string;
  children: (Component | FlexContainerConfig)[];
  properties: {
    flexDirection: 'row' | 'column' | 'row-reverse' | 'column-reverse';
    justifyContent: string;
    alignItems: string;
    flexWrap: 'nowrap' | 'wrap' | 'wrap-reverse';
    gap: number;
    padding: number;
    backgroundColor?: string;
    borderRadius?: number;
    minHeight?: number;
  };
  responsiveProperties?: {
    mobile?: Partial<FlexContainerConfig['properties']>;
    tablet?: Partial<FlexContainerConfig['properties']>;
    desktop?: Partial<FlexContainerConfig['properties']>;
  };
}

export interface ResponsiveLayoutResult {
  rootContainers: FlexContainerConfig[];
  componentUpdates: Map<string, {
    responsivePosition: ResponsivePosition;
    parentContainerId?: string;
  }>;
  layoutScore: number;
  recommendations: string[];
}

export class ResponsiveLayoutAnalyzer {
  private readonly ALIGNMENT_THRESHOLD = 10; // pixels
  private readonly SPACING_VARIANCE_THRESHOLD = 0.2; // 20% variance allowed
  private readonly MIN_GROUP_SIZE = 2;
  private layoutPatterns: any[] = [];

  constructor() {
    this.loadLayoutPatterns();
  }

  /**
   * Load training patterns for pattern matching
   */
  private loadLayoutPatterns(): void {
    try {
      // Import layout patterns for matching
      const patterns = require('@/data/layout-training-examples.json');
      this.layoutPatterns = patterns.patterns || [];
    } catch (error) {
      console.warn('Could not load layout patterns:', error);
      this.layoutPatterns = [];
    }
  }

  /**
   * Analyze components and generate responsive layout
   */
  public analyzeLayout(
    components: Component[],
    canvasWidth: number = 1200,
    canvasHeight: number = 800
  ): ResponsiveLayoutResult {
    if (!components || components.length === 0) {
      return {
        rootContainers: [],
        componentUpdates: new Map(),
        layoutScore: 0,
        recommendations: ['No components to analyze']
      };
    }

    // Step 0: Try to match against known patterns first
    const patternMatch = this.matchLayoutPattern(components);
    if (patternMatch) {
      console.log('🎯 Matched layout pattern:', patternMatch.name);
      return this.applyPatternTemplate(patternMatch, components);
    }

    // Step 1: Detect layout patterns
    const patterns = this.detectLayoutPatterns(components);

    // Step 2: Build container hierarchy
    const containers = this.buildContainerHierarchy(patterns, components);

    // Step 3: Generate responsive properties
    const componentUpdates = this.generateResponsiveProperties(
      components,
      containers,
      canvasWidth,
      canvasHeight
    );

    // Step 4: Calculate layout quality score
    const layoutScore = this.calculateLayoutScore(patterns, components);

    // Step 5: Generate recommendations
    const recommendations = this.generateRecommendations(patterns, components);

    return {
      rootContainers: containers,
      componentUpdates,
      layoutScore,
      recommendations
    };
  }

  /**
   * Match components against known layout patterns
   */
  private matchLayoutPattern(components: Component[]): any | null {
    if (!this.layoutPatterns || this.layoutPatterns.length === 0) {
      return null;
    }

    // Check for header pattern: title in center, text on right
    if (this.isHeaderPattern(components)) {
      const headerPattern = this.layoutPatterns.find(p => p.id === 'header-center-title-right-text');
      if (headerPattern) {
        return headerPattern;
      }
    }

    // Check for other patterns
    const patternScores = this.layoutPatterns.map(pattern => ({
      pattern,
      score: this.calculatePatternSimilarity(components, pattern)
    }));

    // Sort by score and get best match
    patternScores.sort((a, b) => b.score - a.score);

    // Return pattern if similarity is high enough (threshold: 70%)
    if (patternScores[0] && patternScores[0].score > 0.7) {
      return patternScores[0].pattern;
    }

    return null;
  }

  /**
   * Check if components match a header pattern
   */
  private isHeaderPattern(components: Component[]): boolean {
    if (components.length < 2) return false;

    // Look for text components
    const textComponents = components.filter(c => c.type === ComponentType.TEXT);
    if (textComponents.length < 2) return false;

    // Sort by Y position to find header row
    const sortedByY = [...textComponents].sort((a, b) => a.position.y - b.position.y);

    // Check if components are in same horizontal row (similar Y)
    const firstY = sortedByY[0].position.y;
    const inSameRow = sortedByY.filter(c =>
      Math.abs(c.position.y - firstY) <= this.ALIGNMENT_THRESHOLD
    );

    if (inSameRow.length >= 2) {
      // Check for center and right alignment pattern
      const canvasCenter = 600; // Assuming standard canvas width

      // Check if any component is near center
      const hasCenter = inSameRow.some(c =>
        Math.abs((c.position.x + c.size.width / 2) - canvasCenter) < 100
      );

      // Check if any component is on the right
      const hasRight = inSameRow.some(c => c.position.x > canvasCenter + 200);

      return hasCenter && hasRight;
    }

    return false;
  }

  /**
   * Calculate similarity between components and a pattern
   */
  private calculatePatternSimilarity(components: Component[], pattern: any): number {
    let score = 0;
    const maxScore = 5;

    // Check component count similarity
    const patternComponentCount = this.countPatternComponents(pattern.structure);
    const countDiff = Math.abs(components.length - patternComponentCount);
    if (countDiff === 0) score += 1;
    else if (countDiff <= 2) score += 0.5;

    // Check component type similarity
    const componentTypes = new Set(components.map(c => c.type));
    const patternTypes = this.extractPatternTypes(pattern.structure);
    const typeOverlap = this.calculateSetOverlap(componentTypes, patternTypes);
    score += typeOverlap;

    // Check layout structure (row/column)
    const hasRow = components.some((c, i) => {
      return components.some((other, j) =>
        i !== j && Math.abs(c.position.y - other.position.y) < this.ALIGNMENT_THRESHOLD
      );
    });
    const hasColumn = components.some((c, i) => {
      return components.some((other, j) =>
        i !== j && Math.abs(c.position.x - other.position.x) < this.ALIGNMENT_THRESHOLD
      );
    });

    const patternDirection = pattern.structure?.properties?.flexDirection;
    if ((hasRow && patternDirection === 'row') || (hasColumn && patternDirection === 'column')) {
      score += 1;
    }

    // Check position pattern (e.g., centered, spread out)
    if (pattern.category === 'header' && this.isHeaderPattern(components)) {
      score += 2;
    }

    return score / maxScore;
  }

  /**
   * Count components in a pattern structure
   */
  private countPatternComponents(structure: any): number {
    if (!structure) return 0;
    let count = 1;
    if (structure.children && Array.isArray(structure.children)) {
      for (const child of structure.children) {
        count += this.countPatternComponents(child);
      }
    }
    return count;
  }

  /**
   * Extract component types from pattern
   */
  private extractPatternTypes(structure: any): Set<ComponentType> {
    const types = new Set<ComponentType>();
    if (!structure) return types;

    if (structure.type === 'text') types.add(ComponentType.TEXT);
    else if (structure.type === 'button') types.add(ComponentType.BUTTON);
    else if (structure.type === 'image') types.add(ComponentType.IMAGE);
    else if (structure.type === 'flex-container') types.add(ComponentType.FLEX_CONTAINER);

    if (structure.children && Array.isArray(structure.children)) {
      for (const child of structure.children) {
        const childTypes = this.extractPatternTypes(child);
        childTypes.forEach(t => types.add(t));
      }
    }

    return types;
  }

  /**
   * Calculate overlap between two sets
   */
  private calculateSetOverlap<T>(set1: Set<T>, set2: Set<T>): number {
    if (set1.size === 0 || set2.size === 0) return 0;
    let overlap = 0;
    set1.forEach(item => {
      if (set2.has(item)) overlap++;
    });
    return overlap / Math.max(set1.size, set2.size);
  }

  /**
   * Apply a matched pattern template to components
   */
  private applyPatternTemplate(
    pattern: any,
    components: Component[]
  ): ResponsiveLayoutResult {
    const componentUpdates = new Map<string, { responsivePosition: ResponsivePosition; parentContainerId?: string }>();

    // Create the container structure from the pattern
    const container = this.createContainerFromPattern(pattern.structure, components);

    // Map components to the pattern structure
    const mappedComponents = this.mapComponentsToPattern(components, pattern.structure);

    // Update each component with responsive properties
    mappedComponents.forEach(({ component, responsiveProps, parentContainerId }) => {
      componentUpdates.set(component.id, {
        responsivePosition: responsiveProps,
        parentContainerId
      });
    });

    return {
      rootContainers: [container],
      componentUpdates,
      layoutScore: 95, // High score for pattern match
      recommendations: [`Layout matched pattern: ${pattern.name}`]
    };
  }

  /**
   * Create a FlexContainer from a pattern structure
   */
  private createContainerFromPattern(patternStructure: any, components: Component[]): FlexContainerConfig {
    // Recursively create nested containers
    const createNestedContainer = (structure: any): FlexContainerConfig | Component => {
      if (structure.type === 'flex-container') {
        const children: (FlexContainerConfig | Component)[] = [];

        if (structure.children && Array.isArray(structure.children)) {
          structure.children.forEach((child: any) => {
            if (typeof child === 'object') {
              children.push(createNestedContainer(child));
            }
          });
        }

        return {
          id: structure.id || uuidv4(),
          type: 'flex-container',
          name: structure.name || 'Container',
          children,
          properties: {
            flexDirection: structure.properties?.flexDirection || 'row',
            justifyContent: structure.properties?.justifyContent || 'flex-start',
            alignItems: structure.properties?.alignItems || 'stretch',
            flexWrap: structure.properties?.flexWrap || 'nowrap',
            gap: structure.properties?.gap || 16,
            padding: structure.properties?.padding || 0,
            backgroundColor: structure.properties?.backgroundColor || 'transparent',
            borderRadius: structure.properties?.borderRadius || 0,
            minHeight: structure.properties?.minHeight || 0
          },
          responsiveProperties: structure.responsivePosition?.breakpoints
        } as FlexContainerConfig;
      } else {
        // For non-container components, find matching component from provided list
        const matchingComponent = components.find(c =>
          c.type === (structure.type === 'text' ? ComponentType.TEXT :
                      structure.type === 'button' ? ComponentType.BUTTON :
                      structure.type === 'image' ? ComponentType.IMAGE :
                      ComponentType.CUSTOM)
        );
        return matchingComponent || components[0];
      }
    };

    // Create the root container with nested structure
    const container = createNestedContainer(patternStructure) as FlexContainerConfig;
    return container;
  }

  /**
   * Map actual components to pattern structure
   */
  private mapComponentsToPattern(
    components: Component[],
    patternStructure: any
  ): Array<{ component: Component; responsiveProps: ResponsivePosition; parentContainerId?: string }> {
    const mapped: Array<{ component: Component; responsiveProps: ResponsivePosition; parentContainerId?: string }> = [];

    // Special handling for header pattern
    if (patternStructure.id === 'header-container-001') {
      // Sort components to identify title and secondary text
      const textComponents = components.filter(c => c.type === ComponentType.TEXT);

      // Find the title (usually larger font or centered)
      const titleComponent = textComponents.find(c => {
        const props = c.properties as any;
        return props?.fontSize > 20 ||
               (c.position.x > 400 && c.position.x < 800); // Near center
      });

      // Find secondary text (on the right)
      const secondaryComponent = textComponents.find(c => {
        return c !== titleComponent && c.position.x > 800;
      });

      // Map components with proper responsive properties
      if (titleComponent) {
        mapped.push({
          component: titleComponent,
          responsiveProps: {
            strategy: 'flex',
            layoutMode: 'flex',
            responsiveSize: {
              width: 'auto',
              height: 'auto'
            }
          },
          parentContainerId: patternStructure.id
        });
      }

      if (secondaryComponent) {
        mapped.push({
          component: secondaryComponent,
          responsiveProps: {
            strategy: 'flex',
            layoutMode: 'flex',
            responsiveSize: {
              width: 'auto',
              height: 'auto'
            }
          },
          parentContainerId: patternStructure.id
        });
      }

      // Map other components
      components.filter(c => c !== titleComponent && c !== secondaryComponent).forEach(c => {
        mapped.push({
          component: c,
          responsiveProps: {
            strategy: 'flex',
            layoutMode: 'flex',
            responsiveSize: {
              width: 'auto',
              height: 'auto'
            }
          },
          parentContainerId: patternStructure.id
        });
      });
    } else {
      // Generic mapping for other patterns
      components.forEach(component => {
        mapped.push({
          component,
          responsiveProps: {
            strategy: 'flex',
            layoutMode: 'flex',
            responsiveSize: {
              width: component.size.width > 300 ? '100%' : 'auto',
              height: 'auto',
              minHeight: component.size.height
            }
          },
          parentContainerId: patternStructure.id
        });
      });
    }

    return mapped;
  }

  /**
   * Detect layout patterns in components
   */
  private detectLayoutPatterns(components: Component[]): LayoutPattern[] {
    const patterns: LayoutPattern[] = [];

    // Sort components by position for easier pattern detection
    const sortedByY = [...components].sort((a, b) => a.position.y - b.position.y);
    const sortedByX = [...components].sort((a, b) => a.position.x - b.position.x);

    // Detect rows (horizontally aligned components)
    const rows = this.detectRows(sortedByY);
    patterns.push(...rows);

    // Detect columns (vertically aligned components)
    const columns = this.detectColumns(sortedByX);
    patterns.push(...columns);

    // Detect grids (regular patterns of rows and columns)
    const grids = this.detectGrids(components);
    patterns.push(...grids);

    // Merge overlapping patterns
    return this.mergePatterns(patterns);
  }

  /**
   * Detect row patterns
   */
  private detectRows(components: Component[]): LayoutPattern[] {
    const rows: LayoutPattern[] = [];
    const processed = new Set<string>();

    for (const component of components) {
      if (processed.has(component.id)) continue;

      const row: Component[] = [component];
      processed.add(component.id);

      // Find other components in the same row
      for (const other of components) {
        if (processed.has(other.id)) continue;

        // Check if vertically aligned
        if (Math.abs(component.position.y - other.position.y) <= this.ALIGNMENT_THRESHOLD) {
          row.push(other);
          processed.add(other.id);
        }
      }

      if (row.length >= this.MIN_GROUP_SIZE) {
        // Sort by X position
        row.sort((a, b) => a.position.x - b.position.x);

        // Calculate spacing
        const spacings: number[] = [];
        for (let i = 0; i < row.length - 1; i++) {
          const gap = row[i + 1].position.x - (row[i].position.x + row[i].size.width);
          spacings.push(gap);
        }

        const avgSpacing = spacings.length > 0
          ? spacings.reduce((a, b) => a + b, 0) / spacings.length
          : 16;

        // Determine alignment
        const avgY = row.reduce((sum, c) => sum + c.position.y, 0) / row.length;
        const centerY = row.reduce((sum, c) => sum + c.position.y + c.size.height / 2, 0) / row.length;

        let verticalAlign: 'top' | 'center' | 'bottom' = 'top';
        if (Math.abs(centerY - avgY - row[0].size.height / 2) < this.ALIGNMENT_THRESHOLD) {
          verticalAlign = 'center';
        }

        rows.push({
          type: 'row',
          components: row.map(c => c.id),
          bounds: this.calculateBounds(row),
          spacing: {
            horizontal: avgSpacing,
            vertical: 0
          },
          alignment: {
            horizontal: 'left',
            vertical: verticalAlign
          }
        });
      }
    }

    return rows;
  }

  /**
   * Detect column patterns
   */
  private detectColumns(components: Component[]): LayoutPattern[] {
    const columns: LayoutPattern[] = [];
    const processed = new Set<string>();

    for (const component of components) {
      if (processed.has(component.id)) continue;

      const column: Component[] = [component];
      processed.add(component.id);

      // Find other components in the same column
      for (const other of components) {
        if (processed.has(other.id)) continue;

        // Check if horizontally aligned
        if (Math.abs(component.position.x - other.position.x) <= this.ALIGNMENT_THRESHOLD) {
          column.push(other);
          processed.add(other.id);
        }
      }

      if (column.length >= this.MIN_GROUP_SIZE) {
        // Sort by Y position
        column.sort((a, b) => a.position.y - b.position.y);

        // Calculate spacing
        const spacings: number[] = [];
        for (let i = 0; i < column.length - 1; i++) {
          const gap = column[i + 1].position.y - (column[i].position.y + column[i].size.height);
          spacings.push(gap);
        }

        const avgSpacing = spacings.length > 0
          ? spacings.reduce((a, b) => a + b, 0) / spacings.length
          : 16;

        columns.push({
          type: 'column',
          components: column.map(c => c.id),
          bounds: this.calculateBounds(column),
          spacing: {
            horizontal: 0,
            vertical: avgSpacing
          },
          alignment: {
            horizontal: 'left',
            vertical: 'top'
          }
        });
      }
    }

    return columns;
  }

  /**
   * Detect grid patterns
   */
  private detectGrids(components: Component[]): LayoutPattern[] {
    const grids: LayoutPattern[] = [];

    // Simple grid detection based on regular spacing
    // Group components by similar Y positions (rows)
    const rowGroups = new Map<number, Component[]>();

    for (const component of components) {
      const rowY = Math.round(component.position.y / 50) * 50; // Quantize to 50px grid
      if (!rowGroups.has(rowY)) {
        rowGroups.set(rowY, []);
      }
      rowGroups.get(rowY)!.push(component);
    }

    // Check if we have a regular grid
    const rowCounts = Array.from(rowGroups.values()).map(r => r.length);
    const isRegularGrid = rowCounts.length > 1 &&
      new Set(rowCounts).size === 1 && // All rows have same count
      rowCounts[0] > 1; // More than one column

    if (isRegularGrid && rowGroups.size >= 2) {
      const allGridComponents = Array.from(rowGroups.values()).flat();

      grids.push({
        type: 'grid',
        components: allGridComponents.map(c => c.id),
        bounds: this.calculateBounds(allGridComponents),
        spacing: {
          horizontal: 20,
          vertical: 20
        },
        alignment: {
          horizontal: 'left',
          vertical: 'top'
        }
      });
    }

    return grids;
  }

  /**
   * Calculate bounding box for components
   */
  private calculateBounds(components: Component[]): LayoutPattern['bounds'] {
    if (components.length === 0) {
      return { x: 0, y: 0, width: 0, height: 0 };
    }

    const minX = Math.min(...components.map(c => c.position.x));
    const minY = Math.min(...components.map(c => c.position.y));
    const maxX = Math.max(...components.map(c => c.position.x + c.size.width));
    const maxY = Math.max(...components.map(c => c.position.y + c.size.height));

    return {
      x: minX,
      y: minY,
      width: maxX - minX,
      height: maxY - minY
    };
  }

  /**
   * Merge overlapping patterns
   */
  private mergePatterns(patterns: LayoutPattern[]): LayoutPattern[] {
    // Remove duplicate component references
    const componentToPattern = new Map<string, LayoutPattern>();
    const mergedPatterns: LayoutPattern[] = [];

    for (const pattern of patterns) {
      let hasConflict = false;
      const conflictingComponents: string[] = [];

      for (const componentId of pattern.components) {
        if (componentToPattern.has(componentId)) {
          hasConflict = true;
          conflictingComponents.push(componentId);
        }
      }

      if (!hasConflict) {
        mergedPatterns.push(pattern);
        pattern.components.forEach(id => componentToPattern.set(id, pattern));
      } else {
        // Keep the pattern if it has more components
        const existingPattern = componentToPattern.get(conflictingComponents[0]);
        if (existingPattern && pattern.components.length > existingPattern.components.length) {
          // Remove old pattern
          const oldIndex = mergedPatterns.indexOf(existingPattern);
          if (oldIndex !== -1) {
            mergedPatterns.splice(oldIndex, 1);
          }

          // Add new pattern
          mergedPatterns.push(pattern);
          pattern.components.forEach(id => componentToPattern.set(id, pattern));
        }
      }
    }

    return mergedPatterns;
  }

  /**
   * Build container hierarchy from patterns
   */
  private buildContainerHierarchy(
    patterns: LayoutPattern[],
    components: Component[]
  ): FlexContainerConfig[] {
    const containers: FlexContainerConfig[] = [];
    const processedComponents = new Set<string>();

    // Create containers for each pattern
    for (const pattern of patterns) {
      const container = this.createFlexContainer(pattern, components);
      if (container) {
        containers.push(container);
        pattern.components.forEach(id => processedComponents.add(id));
      }
    }

    // Handle ungrouped components
    const ungroupedComponents = components.filter(c => !processedComponents.has(c.id));
    if (ungroupedComponents.length > 0) {
      // Create a default column container for ungrouped components
      const defaultContainer: FlexContainerConfig = {
        id: uuidv4(),
        type: 'flex-container',
        name: 'Main Container',
        children: ungroupedComponents,
        properties: {
          flexDirection: 'column',
          justifyContent: 'flex-start',
          alignItems: 'stretch',
          flexWrap: 'nowrap',
          gap: 16,
          padding: 20,
          minHeight: 400
        },
        responsiveProperties: {
          mobile: {
            padding: 12,
            gap: 12
          },
          tablet: {
            padding: 16,
            gap: 14
          }
        }
      };
      containers.push(defaultContainer);
    }

    return containers;
  }

  /**
   * Create FlexContainer from pattern
   */
  private createFlexContainer(
    pattern: LayoutPattern,
    components: Component[]
  ): FlexContainerConfig | null {
    const patternComponents = components.filter(c => pattern.components.includes(c.id));
    if (patternComponents.length === 0) return null;

    const flexDirection = pattern.type === 'row' ? 'row' :
                         pattern.type === 'column' ? 'column' :
                         'row';

    const justifyContent = pattern.alignment.horizontal === 'center' ? 'center' :
                           pattern.alignment.horizontal === 'right' ? 'flex-end' :
                           pattern.alignment.horizontal === 'space-between' ? 'space-between' :
                           'flex-start';

    const alignItems = pattern.alignment.vertical === 'center' ? 'center' :
                       pattern.alignment.vertical === 'bottom' ? 'flex-end' :
                       pattern.alignment.vertical === 'stretch' ? 'stretch' :
                       'flex-start';

    const gap = pattern.type === 'row' ? pattern.spacing.horizontal : pattern.spacing.vertical;

    const container: FlexContainerConfig = {
      id: uuidv4(),
      type: 'flex-container',
      name: `${pattern.type.charAt(0).toUpperCase() + pattern.type.slice(1)} Container`,
      children: patternComponents,
      properties: {
        flexDirection,
        justifyContent,
        alignItems,
        flexWrap: pattern.type === 'grid' ? 'wrap' : 'nowrap',
        gap: Math.max(8, Math.round(gap)),
        padding: 16,
        minHeight: pattern.bounds.height
      },
      responsiveProperties: {
        mobile: {
          flexDirection: pattern.type === 'row' ? 'column' : flexDirection,
          gap: Math.max(8, Math.round(gap * 0.75)),
          padding: 12
        },
        tablet: {
          gap: Math.max(8, Math.round(gap * 0.9)),
          padding: 14
        }
      }
    };

    return container;
  }

  /**
   * Generate responsive properties for components
   */
  private generateResponsiveProperties(
    components: Component[],
    containers: FlexContainerConfig[],
    canvasWidth: number,
    canvasHeight: number
  ): Map<string, { responsivePosition: ResponsivePosition; parentContainerId?: string }> {
    const updates = new Map<string, { responsivePosition: ResponsivePosition; parentContainerId?: string }>();

    // Map components to their containers
    const componentToContainer = new Map<string, string>();
    for (const container of containers) {
      this.mapComponentsToContainer(container, componentToContainer);
    }

    // Generate responsive positions for each component
    for (const component of components) {
      const containerId = componentToContainer.get(component.id);
      const container = containers.find(c => c.id === containerId);

      // Determine positioning strategy based on container
      const strategy: PositioningStrategy = container ? 'flex' : 'absolute';

      const responsivePosition: ResponsivePosition = {
        strategy,
        responsiveSize: {
          width: component.size.width > 300 ? `${(component.size.width / canvasWidth) * 100}%` : component.size.width,
          height: component.size.height,
          minWidth: 50,
          maxWidth: '100%',
          aspectRatio: component.size.width / component.size.height
        },
        constraints: {
          pinToEdges: {
            left: component.position.x < 50,
            right: (canvasWidth - (component.position.x + component.size.width)) < 50,
            top: component.position.y < 50,
            bottom: (canvasHeight - (component.position.y + component.size.height)) < 50
          },
          percentagePosition: {
            x: (component.position.x / canvasWidth) * 100,
            y: (component.position.y / canvasHeight) * 100
          }
        },
        breakpoints: {
          mobile: {
            size: {
              width: '100%',
              minHeight: 40
            },
            style: {
              padding: '8px'
            }
          },
          tablet: {
            size: {
              width: component.size.width > 400 ? '48%' : `${component.size.width}px`,
              height: component.size.height
            },
            style: {
              padding: '12px'
            }
          },
          desktop: {
            size: {
              width: component.size.width > 300 ? `${(component.size.width / canvasWidth) * 100}%` : `${component.size.width}px`,
              height: component.size.height
            },
            style: {
              padding: '16px'
            }
          }
        }
      };

      // If component is in a flex container, set flex properties directly on ResponsivePosition
      if (container) {
        responsivePosition.layoutMode = 'flex';
        responsivePosition.flexDirection = container.properties.flexDirection;
        responsivePosition.justifyContent = container.properties.justifyContent as
          'flex-start' | 'flex-end' | 'center' | 'space-between' | 'space-around' | 'space-evenly';
        responsivePosition.alignItems = container.properties.alignItems as
          'flex-start' | 'flex-end' | 'center' | 'baseline' | 'stretch';
        responsivePosition.flexWrap = container.properties.flexWrap;
        responsivePosition.gap = container.properties.gap;
      }

      updates.set(component.id, {
        responsivePosition,
        parentContainerId: containerId
      });
    }

    return updates;
  }

  /**
   * Map components to their container
   */
  private mapComponentsToContainer(
    container: FlexContainerConfig,
    componentToContainer: Map<string, string>
  ): void {
    for (const child of container.children) {
      if ('id' in child && child.id) {
        componentToContainer.set(child.id, container.id);
      }
      if ('children' in child && Array.isArray(child.children)) {
        this.mapComponentsToContainer(child as FlexContainerConfig, componentToContainer);
      }
    }
  }

  /**
   * Calculate layout quality score
   */
  private calculateLayoutScore(patterns: LayoutPattern[], components: Component[]): number {
    let score = 70; // Base score

    // Bonus for detected patterns
    score += Math.min(20, patterns.length * 5);

    // Bonus for consistent spacing
    const spacingConsistency = this.calculateSpacingConsistency(patterns);
    score += spacingConsistency * 10;

    // Penalty for ungrouped components
    const groupedComponents = new Set(patterns.flatMap(p => p.components));
    const ungroupedRatio = (components.length - groupedComponents.size) / components.length;
    score -= ungroupedRatio * 20;

    return Math.max(0, Math.min(100, Math.round(score)));
  }

  /**
   * Calculate spacing consistency
   */
  private calculateSpacingConsistency(patterns: LayoutPattern[]): number {
    if (patterns.length === 0) return 0.5;

    const allSpacings: number[] = [];
    for (const pattern of patterns) {
      if (pattern.spacing.horizontal > 0) allSpacings.push(pattern.spacing.horizontal);
      if (pattern.spacing.vertical > 0) allSpacings.push(pattern.spacing.vertical);
    }

    if (allSpacings.length === 0) return 0.5;

    const mean = allSpacings.reduce((a, b) => a + b, 0) / allSpacings.length;
    const variance = allSpacings.reduce((sum, val) => sum + Math.pow(val - mean, 2), 0) / allSpacings.length;
    const stdDev = Math.sqrt(variance);
    const cv = stdDev / mean;

    // Lower coefficient of variation = more consistent
    return Math.max(0, Math.min(1, 1 - cv));
  }

  /**
   * Generate layout recommendations
   */
  private generateRecommendations(patterns: LayoutPattern[], components: Component[]): string[] {
    const recommendations: string[] = [];

    // Check for alignment issues
    const alignmentIssues = this.checkAlignmentIssues(components);
    if (alignmentIssues > 0) {
      recommendations.push(`Consider aligning ${alignmentIssues} components for better visual consistency`);
    }

    // Check for spacing consistency
    if (patterns.length > 1) {
      const spacings = patterns.map(p => p.spacing.horizontal || p.spacing.vertical);
      const uniqueSpacings = new Set(spacings);
      if (uniqueSpacings.size > 3) {
        recommendations.push('Use consistent spacing between components for better visual rhythm');
      }
    }

    // Check for responsive considerations
    const largeComponents = components.filter(c => c.size.width > 600);
    if (largeComponents.length > 0) {
      recommendations.push(`${largeComponents.length} component(s) may need responsive adjustments for mobile devices`);
    }

    // Suggest grid layout if many components
    if (components.length > 8 && patterns.filter(p => p.type === 'grid').length === 0) {
      recommendations.push('Consider using a grid layout for better organization of multiple components');
    }

    return recommendations;
  }

  /**
   * Check for alignment issues
   */
  private checkAlignmentIssues(components: Component[]): number {
    let issues = 0;

    for (let i = 0; i < components.length - 1; i++) {
      for (let j = i + 1; j < components.length; j++) {
        const c1 = components[i];
        const c2 = components[j];

        // Check near-alignment (within threshold but not exactly aligned)
        const xDiff = Math.abs(c1.position.x - c2.position.x);
        const yDiff = Math.abs(c1.position.y - c2.position.y);

        if ((xDiff > 0 && xDiff < this.ALIGNMENT_THRESHOLD) ||
            (yDiff > 0 && yDiff < this.ALIGNMENT_THRESHOLD)) {
          issues++;
        }
      }
    }

    return issues;
  }
}
